import AddIcon from "@mui/icons-material/Add"
import { Button, styled } from "@mui/material"
import {
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarProps,
} from "@mui/x-data-grid"
import { ReactElement, ReactNode } from "react"

import ColumnReorderInterface from "./columnReorderInterface"

/** Available toolbar buttons for the AdminDataGrid */
export type ToolbarButton =
    | "create"
    | "columns"
    | "reorder"
    | "filter"
    | "export"

/** All toolbar buttons — the default when no selection is given */
export const ALL_TOOLBAR_BUTTONS: ToolbarButton[] = [
    "create",
    "columns",
    "reorder",
    "filter",
    "export",
]

const StyledToolbarButton = styled(Button)(({ theme }) => ({
    zIndex: 1,
    transition: theme.transitions.create(["opacity"]),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius,
    },
}))

// Custom Toolbar with Column Reorder functionality
export interface CustomToolbarProps extends GridToolbarProps {
    columns: GridColDef[]
    onColumnOrderChange: (newOrder: string[]) => void
    onCreateClick?: () => void
    /** Which toolbar buttons to display (default: all) */
    toolbarButtons?: ToolbarButton[]
    customToolbarButtons?: ReactNode
    exportFileName?: string
}

export const CustomToolbar = ({
    columns,
    onColumnOrderChange,
    onCreateClick,
    toolbarButtons = ALL_TOOLBAR_BUTTONS,
    customToolbarButtons,
    exportFileName = "ENA Connect Direct",
}: CustomToolbarProps): ReactElement => (
    <GridToolbarContainer
        sx={{
            padding: "8px 16px",
            "& .MuiButton-root": {
                fontSize: "0.875rem",
                color: "text.secondary",
                "&:hover": {
                    backgroundColor: "action.hover",
                    color: "primary.main",
                },
            },
            "& .MuiSvgIcon-root": {
                fontSize: "1.2rem",
            },
        }}
    >
        {toolbarButtons.includes("create") && (
            <StyledToolbarButton
                onClick={onCreateClick}
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                data-testid="create-button"
            >
                CREATE
            </StyledToolbarButton>
        )}
        {customToolbarButtons}
        {toolbarButtons.includes("columns") && <GridToolbarColumnsButton />}
        {toolbarButtons.includes("reorder") && (
            <ColumnReorderInterface
                columns={columns}
                onColumnOrderChange={onColumnOrderChange}
            />
        )}
        {toolbarButtons.includes("filter") && <GridToolbarFilterButton />}
        {toolbarButtons.includes("export") && (
            <GridToolbarExport
                csvOptions={{
                    utf8WithBom: true,
                    fileName: exportFileName,
                }}
            />
        )}
    </GridToolbarContainer>
)
